import {AGENCY} from '@/pages/routes'
import {Form, Input, InputNumber, Space, Spin, Switch} from 'antd'
import FormItem from 'antd/es/form/FormItem'
import {AgencyBean} from 'common/api/api'
import {agencyApi} from 'common/api/entitiesApi'
import {AnchorsForm} from 'common/components/AnchorsForm'
import {ButtonsPanel} from 'common/components/ButtonsPanel'
import {FeatureAccessSelector} from 'common/components/dictionary/FeatureAccessSelector.tsx'
import {FormCard} from 'common/components/FormCard'
import {SspSelector} from 'common/components/reloadable/SspSelector.tsx'
import {StatusSwitch} from 'common/components/StatusSwitch.tsx'
import {UiAccessSwitch} from 'common/components/UiAccessSwitch.tsx'
import {useAppContext} from 'common/context/AppContext'
import {apiCall, getCopyName} from 'common/util/Util'
import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

const DOMAIN_RE =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/i

const validateDomain = (_: any, value: any) => {
  if (value === undefined || value === null || value === '') return Promise.resolve()
  if (typeof value !== 'string') return Promise.reject(new Error('Invalid value'))
  if (/\s/.test(value)) return Promise.reject(new Error('Spaces are not allowed'))
  if (value.includes(',')) return Promise.reject(new Error('Only one domain is allowed'))

  return DOMAIN_RE.test(value)
    ? Promise.resolve()
    : Promise.reject(new Error('Please enter a domain without scheme or path (e.g., domain.com)'))
}

const validateDomainList = (_: any, value: any) => {
  if (value === undefined || value === null || value === '') return Promise.resolve()
  if (typeof value !== 'string') return Promise.reject(new Error('Invalid value'))
  if (/\s/.test(value)) return Promise.reject(new Error('Spaces are not allowed'))

  const parts = value.split(',')

  if (parts.some((p) => p === '')) {
    return Promise.reject(new Error('Please enter comma-separated domains (no empty items)'))
  }

  if (parts[0].indexOf('localhost:') == 0) {
      return Promise.resolve()
  }

  return parts.every((d) => DOMAIN_RE.test(d))
    ? Promise.resolve()
    : Promise.reject(new Error('Please enter comma-separated domains without scheme or path (e.g., a.com,b.a.com)'))
}

const AgencyForm = ({ mode }) => {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const [entity, setEntity] = useState<AgencyBean>({
    active: true,
    uiAccess: true,
    advertiserSelfRegistration: false,
  } as AgencyBean)

  const navigateTo = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const idNum = id ? parseInt(id) : 0
  const appContext = useAppContext()

  const deleteAgency = () => {
    apiCall(
      setLoading,
      appContext,
      () => agencyApi.agencyDelete(idNum),
      () => navigateTo(AGENCY),
    )
  }

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const newEntity = { ...entity, ...values }

      apiCall(
        setLoading,
        appContext,
        () => (mode === 'edit' && id ? agencyApi.agencyUpdate(newEntity, idNum) : agencyApi.agencyCreate(newEntity)),
        () => {
          navigateTo(AGENCY)
        },
      )
    })
  }

  useEffect(() => {
    if (mode === 'edit' || mode === 'copy') {
      apiCall(
        setLoading,
        appContext,
        () => agencyApi.agency(idNum),
        (data) => {
          if (data && data.data) {
            const entityData = mode === 'copy' ? { ...data.data, name: getCopyName(data.data.name) } : data.data
            setEntity(entityData)
            form.setFieldsValue(entityData)
          }
        },
      )
    }
  }, [mode, id])

  return (
    <Spin spinning={loading}>
      <AnchorsForm form={form} onFinish={handleSubmit}>
        <FormCard
          title="Agency"
          extra={
            <Space size="middle" align="center">
              <UiAccessSwitch entity={entity} setEntity={setEntity} />
              <StatusSwitch entity={entity} setEntity={setEntity} />
            </Space>
          }
        >
          <FormItem name="name" rules={[{ required: true, message: 'Please input Agency name' }]} label="Name">
            <Input />
          </FormItem>
            <FormItem name="publicId" rules={[{ required: true, message: 'Please input Agency Public Id' }]} label="Public ID">
                <Input />
            </FormItem>
          <FormItem
            name="featureAccess"
            label="Feature Access"
            rules={[{ required: true, message: 'Feature Access is required' }]}
          >
            <FeatureAccessSelector placeholder="Select Features" />
          </FormItem>
          <Form.Item name="revShare" rules={[{ required: true, message: 'RevShare is required' }]} label="System SSPs RevShare">
            <InputNumber step={1} min={0} max={99} style={{ width: '100px' }} addonAfter="%" />
          </Form.Item>
          <FormItem name="sspIds" label="Linked SSPs">
            <SspSelector multiple placeholder="Select SSPs" />
          </FormItem>
          {/*<FormItem name="publisherIds" label="Linked Publishers">*/}
          {/*  <PublisherSelector multiple placeholder="Select Publishers" />*/}
          {/*</FormItem>*/}
          <FormItem
            name="cdnDomain"
            label="CDN Domain"
            rules={[{ validator: validateDomain }]}
          >
            <Input placeholder="domain.com" />
          </FormItem>
          <FormItem
            name="agencyDomains"
            label="Agency Domains"
            rules={[{ validator: validateDomainList }]}
          >
            <Input placeholder="Comma separated (domain.com,sub.domain.com)" />
          </FormItem>
          <FormItem
            name="advertiserDomains"
            label="Advertiser Domains"
            rules={[{ validator: validateDomainList }]}
          >
            <Input placeholder="Comma separated (domain.com,sub.domain.com)" />
          </FormItem>
          <FormItem
            name="advertiserSelfRegistration"
            label="Enable advertiser self-registration"
            valuePropName="checked"
          >
            <Switch size="small" />
          </FormItem>
          <FormItem
            name="endpointDomains"
            label="Endpoint Domains"
            rules={[{ validator: validateDomainList }]}
          >
            <Input placeholder="Comma separated (domain.com,sub.domain.com)" />
          </FormItem>
        </FormCard>

        <ButtonsPanel
          onCancel={() => navigateTo(AGENCY)}
          showDelete={mode === 'edit'}
          onDelete={() => deleteAgency()}
        ></ButtonsPanel>
      </AnchorsForm>
    </Spin>
  )
}

export default AgencyForm
